import { useEffect, useState } from 'react';
import { categoryService } from '../services/category.service';
import {
  buildCategoryLabelMaps,
  CategoryLabelMaps,
} from '../utils/categoryLabels';

let cachedMaps: CategoryLabelMaps | null = null;
let inflight: Promise<CategoryLabelMaps> | null = null;

async function loadCategoryLabelMaps(): Promise<CategoryLabelMaps> {
  if (cachedMaps) return cachedMaps;
  if (inflight) return inflight;

  inflight = (async () => {
    const response = await categoryService.getAllCategories();
    const categories = response.data?.categories || [];
    cachedMaps = buildCategoryLabelMaps(categories);
    return cachedMaps;
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

/**
 * Shared catalog ID/key → name maps for display-only resolution.
 * Safe to call from multiple screens; loads the catalogue once.
 */
export function useCategoryLabelMaps(): {
  maps: CategoryLabelMaps | null;
  loading: boolean;
} {
  const [maps, setMaps] = useState<CategoryLabelMaps | null>(cachedMaps);
  const [loading, setLoading] = useState(!cachedMaps);

  useEffect(() => {
    let cancelled = false;
    setLoading(!cachedMaps);
    loadCategoryLabelMaps()
      .then((result) => {
        if (!cancelled) {
          setMaps(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load category label maps', err);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { maps, loading };
}
